/* eslint-disable react/prop-types */
import { useSwaggerlessContext } from 'Context/SwaggerlessContext';
import React, {
  createContext, useEffect, useState, useContext,
} from 'react';
import PropTypes from 'prop-types';
import fr from 'facet-rest';
import { QueryVal } from 'Helpers/Query';
import useUserContext from 'Context/UserContext';
import Household, { fromAPIResponse } from 'Classes/Household';
import { displayErrorMessage } from 'Helpers/FacetNotificationManager';
import { pickFunction } from 'Helpers/Affordances';

// eslint-disable-next-line spaced-comment
/********************************************************************************************
 * Simple Household Context State
 *
 * A list of all state managed by Simple HouseholdContext. Each piece of state here must
 * be accompanied by a corresponding useState that defaults to the state listed here AND
 * must also have an entry in the reset() function below that resets state when a household
 * is reloaded.
 ********************************************************************************************/
const defaultState = {
  // Household
  householdLoading: false,
  householdId: 0,
  household: new Household(0), // TODO: Can we kill household? we would like to kill it.
  householdResource: null,
  householdSearch: [],
  invoiceReceipts: [],
};

const SimpleHouseholdContext = createContext(defaultState);

function SimpleHouseholdProviderInternal({ children }) {
  const { getHousehold: getHousehold2, updateHousehold: updateHousehold2, updateAccount: updateAccount2 } = useSwaggerlessContext();

  // Household
  const [householdId, setHouseholdId] = useState(
    QueryVal('householdId') || defaultState.householdId,
  );
  const [household, setHousehold] = useState(defaultState.household);
  const [householdResource, setHouseholdResource] = useState(
    defaultState.householdResource,
  );
  const [householdLoading, setHouseholdLoading] = useState(
    defaultState.householdLoading,
  );
  const [householdSearch, setHouseholdSearch] = useState(
    defaultState.householdSearch,
  );

  const [invoiceReceipts, setInvoiceReceipts] = useState(
    defaultState.invoiceReceipts,
  );

  const { userResource } = useUserContext();

  const getPrimaryClient = () => {
    return householdResource
      ?.attr('clients')
      ?.resources()
      .list()
      .find(client => client.attr('isPrimary') === 'true');
  };

  const getSpouseClient = () => {
    return householdResource
      ?.attr('clients')
      ?.resources()
      .list()
      .find(
        client => (client.attr('relationship') === 'spouse'
            || client.attr('relationship') === 'significantOther')
          && client.attr('isPrimary') !== 'true',
      );
  };

  const getDependentClients = () => householdResource
    ?.attr('clients')
    ?.resources()
    .list()
    .find(
      client => client.attr('relationship') === 'child'
          || client.attr('relationship') === 'dependent',
    );

  // Household
  const setHouseholdState = (res) => {
    const hh = fromAPIResponse({ data: res });
    const resource = fr.resource(res);

    setHousehold({ ...hh });
    setHouseholdResource(resource);

    return resource;
  };

  function getHousehold(hId = 0) {
    if (hId !== 0) {
      setHouseholdId(hId);
    }

    const hhId = hId || householdId;

    if (hhId === 0) return Promise.resolve();

    setHouseholdLoading(true);

    return getHousehold2(hhId)
      .then(res => setHouseholdState(res.data))
      .catch((e) => {
        displayErrorMessage(e, 'Error while fetching household');
      })
      .then((resource) => {
        setHouseholdLoading(false);
        return resource;
      });
  }

  const updateHousehold = hh => updateHousehold2(hh.id, hh)
    .then(res => setHouseholdState(res.data))
    .catch((err) => {
      // temporary hack for app support, this needs to be handled in the server
      if (err.response?.body.errors[0].source === 'person_id') {
        displayErrorMessage(
          err,
          'Please update the spouse information to include a name',
        );
      } else {
        displayErrorMessage(err, err.response.body.errors);
      }
      return Promise.reject(err);
    });

  // Accounts
  const updateAccount = acc => updateAccount2(householdId, acc.id, acc).catch((e) => {
    displayErrorMessage(e, `Failed to update account: ${acc.id}`);
  });

  const getInvoiceReceipts = () => {
    if (
      householdResource
      && householdResource.relationship('latestInvoiceReceipts')
      && householdResource.relationship('latestInvoiceReceipts').link('related')
    ) {
      householdResource
        .relationship('latestInvoiceReceipts')
        .link('related')
        .Get()
        .then(res => setInvoiceReceipts(res.resources().list()))
        .catch(err => displayErrorMessage(err, 'Failed to retrieve latest payments'));
    }
  };

  const loadHousehold = () => {
    if (householdId > 0) {
      return getHousehold();
    }

    return Promise.resolve();
  };

  function clearHousehold() {
    setHouseholdResource(defaultState.householdResource);
    setHouseholdId(defaultState.householdId);
  }

  const refreshHouseholdSearch = async (userR) => {
    setHouseholdSearch(defaultState.householdSearch);

    return pickFunction(userR, {
      [fr.relationships.householdSearchResults.name]: async () => {
        return pickFunction(userR, {
          [fr.relationships.default.name]: async () => {
            const householdSearchResp = await userR.relationship(fr.relationships.householdSearchResults.name).link('related').get({ raw: true });
            setHouseholdSearch(householdSearchResp.data);
          },
        });
      },
      [fr.relationships.default.name]: () => {},
    });
  };

  const refreshHouseholdResource = React.useCallback(async () => {
    const [updatedHouseholdRes] = await householdResource.link('self').get();
    setHouseholdResource(updatedHouseholdRes);
  }, [householdResource]);

  useEffect(() => {
    if (!householdResource && userResource && userResource.attr('householdId')) {
      // see if user is a household
      setHouseholdId(userResource.attr('householdId'));
    }

    refreshHouseholdSearch(userResource);
  }, [userResource]);

  useEffect(() => {
    loadHousehold(); // Ignore promise returned here since it would impact the effect by becoming a cleanup

    return () => {
      setHouseholdLoading(defaultState.householdLoading);
      setHouseholdResource(defaultState.householdResource);
      setHousehold(defaultState.household);
    };
  }, [householdId, userResource]);

  const providerValue = {
    loadHousehold,
    householdSearch,
    refreshHouseholdSearch: () => {
      return refreshHouseholdSearch(userResource);
    },
    // Household
    householdLoading,
    setHouseholdLoading,
    householdId,
    setHouseholdId,
    household,
    setHousehold,
    householdResource,
    setHouseholdResource,
    refreshHouseholdResource,
    getHousehold,
    updateHousehold,
    getPrimaryClient,
    getSpouseClient,
    getDependentClients,
    invoiceReceipts,
    getInvoiceReceipts,


    // Accounts
    updateAccount,
    setHouseholdState,
    clearHousehold,
  };

  return (
    <SimpleHouseholdContext.Provider value={providerValue}>
      {children}
    </SimpleHouseholdContext.Provider>
  );
}

export const withHouseholdContextAdapter = Wrapped => (props) => {
  const householdContext = useContext(SimpleHouseholdContext);

  const {
    household,
    householdId,
    householdSearch,
    refreshHouseholdSearch,
    householdResource,
    getPrimaryClient,
    getSpouseClient,
    getDependentClients,
    loadHousehold,
  } = householdContext;

  if (!householdResource && QueryVal('householdId')) {
    // see if householdId is a query param, if it is load it
    // getHousehold(QueryVal('householdId'));
    return <div>Loading Household...</div>;
  }

  const additionalProps = {
    primaryClient: getPrimaryClient(),
    spouseClient: getSpouseClient(),
    dependents: getDependentClients(),
    household,
    householdId,
    householdResource,
    householdSearch,
    refreshHouseholdSearch,
    loadHousehold,
  };

  return (
    <Wrapped
      SimpleHouseholdContext={{ ...householdContext, ...additionalProps }}
      {...props}
      {...additionalProps}
    />
  );
};

SimpleHouseholdProviderInternal.propTypes = {
  children: PropTypes.node.isRequired,
};

export const SimpleHouseholdProvider = SimpleHouseholdProviderInternal;

export default SimpleHouseholdContext;
